import { useMutation, useQuery } from '@tanstack/react-query';
import { RETAIL_API_URL } from 'src/utils/envConfig';
import { Node, Edge } from '@xyflow/react';
import { fetcher } from '@/utils/fetcher';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import { useIntl } from 'react-intl';
import { GeneralSettingsType } from '../LogicEditor/SettingsSidebar/types';

export interface LogicDataResponse {
  nodes: Node[];
  edges: Edge[];
  settings: GeneralSettingsType;
  selectedSensors: {
    buttons: string[];
    buzzers: string[];
    trihows: string[];
  };
}

const fetchLogicData = (unitId: string): Promise<LogicDataResponse> => {
  return fetcher(`${RETAIL_API_URL}/logicEditor/blocks?unitId=${unitId}`, {
    method: 'GET',
  }) as Promise<LogicDataResponse>;
};

export const useLogicData = (unitId?: string) => {
  const { data, isError, isLoading, isSuccess, refetch } =
    useQuery<LogicDataResponse>({
      queryKey: ['logicData', unitId],
      queryFn: () => fetchLogicData(unitId!),
      enabled: Boolean(unitId),
      refetchOnWindowFocus: true,
      refetchOnMount: 'always',
      gcTime: 0,
    });

  return {
    data,
    nodes: data?.nodes ?? [],
    edges: data?.edges ?? [],
    settings: data?.settings,
    selectedSensors: data?.selectedSensors ?? [],
    isSuccess,
    isLoading,
    isError,
    refetch,
  };
};

interface SaveLogicParams {
  settings: any;
  nodes: any[];
  edges: any[];
  selectedSensors: {
    buttons: string[];
    buzzers: string[];
    trihows: string[];
  };
}

export const useSaveLogic = (unitId: string) => {
  const toast = useToast();
  const intl = useIntl();

  const {
    mutateAsync: saveLogic,
    isPending: isLoading,
    error,
  } = useMutation({
    mutationFn: async ({
      settings,
      nodes,
      edges,
      selectedSensors,
    }: SaveLogicParams) => {
      const response = await fetcher<{ statusCode?: number; reason?: string }>(
        `${RETAIL_API_URL}/logicEditor/blocks?unitId=${unitId}`,
        {
          method: 'POST',
          body: JSON.stringify({ settings, nodes, edges, selectedSensors }),
        },
      );
      return response;
    },
    onSuccess: () => {
      toast({
        mode: TOASTTYPE.Success,
        subtitle: intl.formatMessage({
          defaultMessage: 'Your logic has been saved!',
          id: '0xl3++',
        }),
      });
    },
    onError: (err: any) => {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Error! Something went wrong saving the logic.',
          id: 'RW2G2r',
        }),
        subtitle: err.info.message,
        mode: TOASTTYPE.Danger,
      });
      // Propagate Error upwards
      throw new Error('Error saving logic useLogicData:', err.message);
    },
  });

  return { saveLogic, isLoading, error };
};
